import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { TooltipProps } from 'recharts'
import { useTheme } from '../../contexts/ThemeContext'
import { categoryCount, fuelColor, shareOf, visibleTops } from './drivstoff'
import { bucketLabeller, yearLabel } from './series'
import type { DrivstoffTidsserie, FuelTop, LeafCounts } from './types'

const pct = (v: number) => `${v.toLocaleString('nb-NO', { maximumFractionDigits: 1 })} %`
const cell = (v: unknown) => (typeof v === 'number' ? pct(v) : '–')

type ChartRow = { x: number; label: string } & Record<string, number | string | null | undefined>

// Bars fill 80 % of a slot, so slots sit 0.2 apart; Totalt sits 0.2 further out: twice the gap
const BAR_CATEGORY_GAP = '10%'
const TOTAL_OFFSET = 0.2

/** Leaf counts summed over the points */
const sumCounts = (all: LeafCounts[]): LeafCounts =>
  all.reduce<LeafCounts>((acc, c) => {
    for (const [k, v] of Object.entries(c) as [keyof LeafCounts, number | undefined][]) acc[k] = (acc[k] ?? 0) + (v ?? 0)
    return acc
  }, {})

/** One row per category, last year and this year side by side */
function YearColumnsTooltip({ active, payload, tops, categoryName, color, year, previousYear }:
  TooltipProps<number, string> & {
    tops: readonly FuelTop[]
    categoryName: (k: FuelTop) => string
    color: (k: FuelTop) => string
    year: string
    previousYear: string | null
  }) {
  if (!active || !payload?.length) return null
  const row = payload[0].payload as ChartRow
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm shadow-md"
      data-testid="drivstoff-chart-tooltip">
      <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{row.label}</div>
      <table>
        <thead>
          <tr className="text-xs text-gray-500 dark:text-gray-400">
            <th />
            {previousYear && <th className="text-right pl-4 font-medium">{previousYear}</th>}
            <th className="text-right pl-4 font-medium">{year}</th>
          </tr>
        </thead>
        <tbody>
          {tops.map(k => (
            <tr key={k}>
              <td className="pr-2" style={{ color: color(k) }}>{categoryName(k)}</td>
              {previousYear && <td className="text-right pl-4 tabular-nums text-gray-500 dark:text-gray-400">{cell(row[`prev_${k}`])}</td>}
              <td className="text-right pl-4 tabular-nums text-gray-900 dark:text-gray-100">{cell(row[k])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Teknisk › Drivstoff › Oversikt: share per category as 100 % stacked bars.
 * Each bucket pairs last year's bar (faded, left) with this year's (#905);
 * a period without last year shows this year's bar alone.
 */
export default function DrivstoffChart({ series }: { series: DrivstoffTidsserie }) {
  const { t } = useTranslation('teknisk')
  const { isDark } = useTheme()
  const tops = visibleTops(series.ekskluder_elbil)

  const data = useMemo<ChartRow[]>(() => {
    const label = bucketLabeller(series.granularity)
    const rows: ChartRow[] = series.points.map((p, i) => ({
      x: i,
      label: label(p.bucket),
      ...p.shares,
      ...Object.fromEntries(Object.entries(p.previous?.shares ?? {}).map(([k, v]) => [`prev_${k}`, v])),
    }))
    // The whole period as one more pair at the end
    const shown = visibleTops(series.ekskluder_elbil)
    const shares = (counts: LeafCounts, prefix: string) => {
      const whole = shown.reduce((n, k) => n + categoryCount(counts, k), 0)
      return Object.fromEntries(shown.map(k => [`${prefix}${k}`, shareOf(categoryCount(counts, k), whole)]))
    }
    const previousPoints = series.points.flatMap(p => (p.previous ? [p.previous.counts] : []))
    return [
      ...rows,
      {
        x: rows.length + TOTAL_OFFSET,
        label: t('drivstoff.chartTotal'),
        ...shares(sumCounts(series.points.map(p => p.counts)), ''),
        ...(series.previous_available ? shares(sumCounts(previousPoints), 'prev_') : {}),
      },
    ]
  }, [series, t])

  const { current, previous } = series.periods
  const year = yearLabel(current.start_date, current.end_date)
  const previousYear = yearLabel(previous.start_date, previous.end_date)
  const hint = series.previous_available
    ? t(series.ekskluder_elbil ? 'drivstoff.chartHintExcluded' : 'drivstoff.chartHint', { year, previousYear })
    : t(series.ekskluder_elbil ? 'drivstoff.chartNoComparisonExcluded' : 'drivstoff.chartNoComparison')
  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'
  // A thin line in the card colour separates the stacked segments
  const surface = isDark ? '#1e293b' : '#ffffff'
  const bar = (k: (typeof tops)[number], prev: boolean) => (
    <Bar
      key={`${prev ? 'prev' : 'cur'}-${k}`}
      dataKey={prev ? `prev_${k}` : k}
      name={t('drivstoff.seriesName', { category: t(`drivstoff.category.${k}` as const), year: prev ? previousYear : year })}
      stackId={prev ? 'previous' : 'current'}
      fill={fuelColor(k, isDark)}
      fillOpacity={prev ? 0.4 : 1}
      stroke={surface}
      strokeWidth={1}
      isAnimationActive={false}
    />
  )

  return (
    <div className="card p-4" data-testid="drivstoff-chart">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {series.granularity === 'month' ? t('drivstoff.chartTitleMonthly') : t('drivstoff.chartTitleDaily')}
      </h3>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2" data-testid="drivstoff-chart-hint">{hint}</p>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }} barGap={8} barCategoryGap={BAR_CATEGORY_GAP}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} vertical={false} />
            {/* Numeric so Totalt can sit a little further out than the gap between months */}
            <XAxis
              dataKey="x"
              type="number"
              domain={[-0.5, data.length - 1 + TOTAL_OFFSET + 0.5]}
              ticks={data.map(r => r.x)}
              interval={0}
              tickFormatter={(x: number) => data.find(r => r.x === x)?.label ?? ''}
              stroke={axisColor}
              fontSize={12}
            />
            {/* Rounded shares can stack to 100.01; keep the axis at 0–100 anyway */}
            <YAxis domain={[0, 100]} allowDataOverflow ticks={[0, 25, 50, 75, 100]}
              tickFormatter={(v: number) => `${v} %`} stroke={axisColor} fontSize={12} width={48} />
            <Tooltip content={
              <YearColumnsTooltip
                tops={tops}
                categoryName={k => t(`drivstoff.category.${k}` as const)}
                color={k => fuelColor(k, isDark)}
                year={year}
                previousYear={series.previous_available ? previousYear : null}
              />
            } />
            {/* One entry per category: last year's faded bars share the colours, the hint names the years */}
            <Legend payload={tops.map(k => ({
              id: k, type: 'square' as const, value: t(`drivstoff.category.${k}` as const), color: fuelColor(k, isDark),
            }))} />
            {series.previous_available && tops.map(k => bar(k, true))}
            {tops.map(k => bar(k, false))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
